import { Toggle, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { errorMessage, getCurrentUserInfoQueryKey, updateUserSettingsMutation } from "@/api";
import { PageSection } from "@/components/section/SectionBlocks";
import { useCapabilities, useCurrentUser } from "@/features/account/useCurrentUser";
import { MemoryList } from "./MemoryList";

/**
 * "What your agents remember" (agent memory, KG design doc §9): agents keep short private
 * notes about the user to help in later chats. On unless they turn it off; they can see and
 * delete every note here. Only shown where the deployment has agent memory.
 */
export function MemorySection() {
  const { agentMemoryEnabled } = useCapabilities();
  const me = useCurrentUser();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation("settings", { keyPrefix: "memory" });
  const save = useMutation({
    ...updateUserSettingsMutation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: getCurrentUserInfoQueryKey() }),
    onError: (error) => toast.error(errorMessage(error, t("saveFailed"))),
  });
  if (!agentMemoryEnabled || !me.data) return null;

  // Stored snake_case in user.settings; null/absent means on.
  const stored = me.data.user.settings?.agent_memory;
  const on = save.isPending ? Boolean(save.variables?.body.agentMemory) : stored !== false;

  return (
    <PageSection
      title={t("title")}
      description={t("description")}
      actions={
        <Toggle
          aria-label={t("label")}
          checked={on}
          onChange={(checked) => save.mutate({ body: { agentMemory: checked } })}
        />
      }
    >
      <MemoryList />
    </PageSection>
  );
}
